;(() => {
  const configEl = document.getElementById('FreeFishDipGwpConfig')
  if (!configEl) return

  let config
  try {
    config = JSON.parse(configEl.textContent)
  } catch {
    return
  }

  if (!config?.enabled || !config.variantId || !config.code) return

  const CODE = String(config.code).trim().toUpperCase()
  const VARIANT_ID = Number(config.variantId)
  const THRESHOLD = Number(config.threshold)
  const PROPERTY_KEY = config.propertyKey || '_gwp'
  const PROPERTY_VALUE = config.propertyValue || 'freefishdip'
  const GIFT_TITLE = config.giftTitle || 'Smoked Mahi Dip'
  const CART_ROOT = window.Shopify?.routes?.root || '/'

  let syncing = false
  let queued = false
  let pendingCheckoutForm = null
  let lastState = {
    active: false,
    qualified: false,
    gift: null,
    variantItem: null
  }

  function shouldHoldCheckout() {
    if (syncing) return true
    return Boolean(lastState.active && lastState.qualified && !lastState.gift && !lastState.variantItem)
  }

  function formatMoney(cents) {
    const safeCents = Math.max(0, Number(cents) || 0)
    if (window.Shopify?.formatMoney && config.moneyFormat) {
      const formatted = window.Shopify.formatMoney(safeCents, config.moneyFormat)
      if (safeCents % 100 === 0) return formatted.replace(/[.,]00$/, '')
      return formatted
    }
    const dollars = safeCents / 100
    return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`
  }

  function urlHasCode() {
    const params = new URLSearchParams(window.location.search)
    return (params.get('discount') || '').trim().toUpperCase() === CODE
  }

  function normalizeCode(discount) {
    return String(discount?.code || '')
      .trim()
      .toUpperCase()
  }

  function otherDiscountApplied(cart) {
    return (cart?.discount_codes || []).some((discount) => {
      return normalizeCode(discount) !== CODE && Boolean(discount.applicable)
    })
  }

  function hasCode(cart) {
    if (otherDiscountApplied(cart)) return false

    return (cart?.discount_codes || []).some((discount) => {
      return normalizeCode(discount) === CODE
    })
  }

  function isGiftItem(item) {
    const properties = item?.properties || {}
    return properties[PROPERTY_KEY] === PROPERTY_VALUE
  }

  function findGiftItem(cart) {
    return (cart?.items || []).find(isGiftItem)
  }

  function findVariantItem(cart) {
    return (cart?.items || []).find((item) => Number(item.variant_id) === VARIANT_ID)
  }

  function qualifyingTotal(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (isGiftItem(item)) return sum
      return sum + (Number(item.original_line_price) || 0)
    }, 0)
  }

  function buildState(cart) {
    const active = !otherDiscountApplied(cart) && (hasCode(cart) || urlHasCode())
    const qualifying = qualifyingTotal(cart)
    const remaining = Math.max(0, THRESHOLD - qualifying)
    const qualified = qualifying >= THRESHOLD
    const gift = findGiftItem(cart)
    const variantItem = findVariantItem(cart)
    let message = ''

    const itemCount = Number(cart?.item_count) || 0

    if (!active) {
      return { active: false, qualified: false, qualifying, remaining, gift, variantItem, message, itemCount }
    }

    if (!qualified) {
      message =
        qualifying > 0
          ? `You're ${formatMoney(remaining)} away from a free ${GIFT_TITLE}. Add that to your cart to unlock it.`
          : `Add ${formatMoney(THRESHOLD)} to your cart to unlock your free ${GIFT_TITLE}.`
    } else if (gift || variantItem) {
      message = `Your free ${GIFT_TITLE} has been added to your cart.`
    } else {
      message = `You've reached ${formatMoney(THRESHOLD)} — your free ${GIFT_TITLE} will be added automatically.`
    }

    return { active, qualified, qualifying, remaining, gift, variantItem, message, itemCount }
  }

  function updateBars(state) {
    lastState = state
    document.querySelectorAll('[data-gwp-bar]').forEach((bar) => {
      if (!state.active || !state.itemCount) {
        bar.hidden = true
        return
      }

      bar.hidden = false
      bar.classList.toggle('cart-gwp--qualified', state.qualified)

      const message = bar.querySelector('[data-gwp-message]')
      if (message) message.textContent = state.message
    })
  }

  async function getCart() {
    const response = await fetch(`${CART_ROOT}cart.js?t=${Date.now()}`, { credentials: 'same-origin' })
    if (!response.ok) throw new Error('Unable to load cart')
    return response.json()
  }

  async function addGift() {
    const response = await fetch(`${CART_ROOT}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [
          {
            id: VARIANT_ID,
            quantity: 1,
            properties: {
              [PROPERTY_KEY]: PROPERTY_VALUE
            }
          }
        ]
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      console.warn('Unable to add free fish dip', error)
      return null
    }

    return getCart()
  }

  async function removeGift(key) {
    const response = await fetch(`${CART_ROOT}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    })

    if (!response.ok) return null
    return getCart()
  }

  function notifyCartChanged(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }))
    document.dispatchEvent(new CustomEvent('cart:build'))
  }

  function continueCheckout() {
    const form = pendingCheckoutForm
    pendingCheckoutForm = null
    if (!form) return

    if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'checkout'
      input.value = '1'
      form.appendChild(input)
    }

    HTMLFormElement.prototype.submit.call(form)
  }

  async function sync(options = {}) {
    if (syncing) {
      queued = true
      return
    }
    syncing = true

    try {
      let cart = options.cart || (await getCart())
      let state = buildState(cart)
      updateBars(state)

      if (!state.active) {
        if (state.gift) {
          const updated = await removeGift(state.gift.key)
          if (updated) {
            cart = updated
            state = buildState(cart)
            updateBars(state)
            notifyCartChanged(cart)
          }
        }
        if (pendingCheckoutForm) continueCheckout()
        return
      }

      let changed = false

      if (state.qualified) {
        if (!state.gift && !state.variantItem) {
          const updated = await addGift()
          if (updated) {
            cart = updated
            state = buildState(cart)
            updateBars(state)
            changed = true
          }
        }
      } else if (state.gift) {
        const updated = await removeGift(state.gift.key)
        if (updated) {
          cart = updated
          state = buildState(cart)
          updateBars(state)
          changed = true
        }
      }

      if (changed) notifyCartChanged(cart)
      if (pendingCheckoutForm) continueCheckout()
    } catch (error) {
      console.warn('FREEFISHDIP gift sync failed', error)
      if (pendingCheckoutForm) continueCheckout()
    } finally {
      syncing = false
      if (queued) {
        queued = false
        sync()
      }
    }
  }

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail?.cart
    if (cart?.items && cart.discount_codes) {
      sync({ cart, skipNotify: true })
      return
    }
    sync({ skipNotify: true })
  })

  document.addEventListener('ajaxProduct:added', () => {
    sync()
  })

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return

      const action = form.getAttribute('action') || ''
      if (!action.includes('/cart')) return
      if (!form.querySelector('[name="checkout"]')) return

      const submitter = event.submitter
      if (submitter && submitter.name && submitter.name !== 'checkout') return
      if (!shouldHoldCheckout()) return

      event.preventDefault()
      pendingCheckoutForm = form
      sync()
    },
    true
  )

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync(), { once: true })
  } else {
    sync()
  }
})()
